import { useState } from 'react';
import * as LoanDB from '@/lib/personalLoansDb';
import { supabase } from '@/lib/supabase';
import CustomerSearchSelect from '@/components/feature/CustomerSearchSelect';

interface NewLoanModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export default function NewLoanModal({ onClose, onSuccess }: NewLoanModalProps) {
  const [form, setForm] = useState({
    borrower_type: 'other' as string,
    borrower_customer_id: '' as string,
    borrower_employee_id: '' as string,
    borrower_name: '',
    borrower_phone: '',
    borrower_second_phone: '',
    borrower_address: '',
    principal_amount: '',
    loan_date: new Date().toLocaleDateString('en-CA'),
    due_date: '',
    payment_method: 'Nakit',
    description: '',
    reason: '',
    guarantor_name: '',
    guarantor_phone: '',
    notes: '',
    cash_account_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const amount = parseFloat(form.principal_amount);
    if (!amount || amount <= 0) {
      setError('Geçerli bir borç tutarı giriniz.');
      return;
    }
    if (!form.borrower_name.trim()) {
      setError('Borç verilen kişi adı zorunludur.');
      return;
    }

    setSaving(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;

      const res = await LoanDB.createPersonalLoan({
        borrower_type: form.borrower_type,
        borrower_customer_id: form.borrower_customer_id || null,
        borrower_employee_id: form.borrower_employee_id || null,
        borrower_name: form.borrower_name.trim(),
        borrower_phone: form.borrower_phone || null,
        borrower_second_phone: form.borrower_second_phone || null,
        borrower_address: form.borrower_address || null,
        principal_amount: amount,
        loan_date: form.loan_date,
        due_date: form.due_date || null,
        payment_method: form.payment_method,
        description: form.description || null,
        reason: form.reason || null,
        guarantor_name: form.guarantor_name || null,
        guarantor_phone: form.guarantor_phone || null,
        cash_account_id: form.cash_account_id || null,
        notes: form.notes || null,
        created_by: userId || null,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setSuccess('Borç kaydı başarıyla oluşturuldu!');
        setTimeout(() => onSuccess(), 1000);
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div>
            <h3 className="text-lg font-semibold text-foreground-900">Yeni Borç Ver</h3>
            <p className="text-xs text-foreground-400 mt-0.5">Elden verilen borç kaydı oluştur</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center gap-2">
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-center gap-2">
              <i className="ri-check-line"></i>
              {success}
            </div>
          )}

          {/* Kişi Türü */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Kişi Türü</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'customer', label: 'Müşteri' },
                { value: 'employee', label: 'Personel' },
                { value: 'supplier', label: 'Tedarikçi' },
                { value: 'other', label: 'Diğer' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChange('borrower_type', opt.value)}
                  className={`px-4 py-2 text-sm rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                    form.borrower_type === opt.value
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200/70 hover:border-primary-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Müşteri Seç */}
          {form.borrower_type === 'customer' && (
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Müşteri Seç</label>
              <CustomerSearchSelect
                value={form.borrower_customer_id}
                onChange={(id, name, phone) => {
                  handleChange('borrower_customer_id', id);
                  if (name) handleChange('borrower_name', name);
                  if (phone) handleChange('borrower_phone', phone);
                }}
              />
            </div>
          )}

          {/* Ad Soyad */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ad Soyad *</label>
            <input
              type="text"
              value={form.borrower_name}
              onChange={e => handleChange('borrower_name', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="Borç verilen kişinin adı soyadı"
            />
          </div>

          {/* Telefon */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Telefon</label>
              <input
                type="text"
                value={form.borrower_phone}
                onChange={e => handleChange('borrower_phone', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="05XX XXX XXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">İkinci Telefon</label>
              <input
                type="text"
                value={form.borrower_second_phone}
                onChange={e => handleChange('borrower_second_phone', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="05XX XXX XXXX"
              />
            </div>
          </div>

          {/* Adres */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Adres</label>
            <input
              type="text"
              value={form.borrower_address}
              onChange={e => handleChange('borrower_address', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="Adres bilgisi"
            />
          </div>

          {/* Tutar + Tarih */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Verilen Borç Tutarı (₺) *</label>
              <input
                type="number"
                value={form.principal_amount}
                onChange={e => handleChange('principal_amount', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Borç Verme Tarihi</label>
              <input
                type="date"
                value={form.loan_date}
                onChange={e => handleChange('loan_date', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              />
            </div>
          </div>

          {/* Vade + Ödeme Şekli */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Son Ödeme Tarihi</label>
              <input
                type="date"
                value={form.due_date}
                onChange={e => handleChange('due_date', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ödeme Şekli</label>
              <select
                value={form.payment_method}
                onChange={e => handleChange('payment_method', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 cursor-pointer"
              >
                <option value="Nakit">Nakit</option>
                <option value="Banka Havalesi">Banka Havalesi</option>
                <option value="Elden">Elden</option>
              </select>
            </div>
          </div>

          {/* Kefil */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Kefil Bilgisi</label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={form.guarantor_name}
                onChange={e => handleChange('guarantor_name', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="Kefil adı"
              />
              <input
                type="text"
                value={form.guarantor_phone}
                onChange={e => handleChange('guarantor_phone', e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
                placeholder="Kefil telefonu"
              />
            </div>
          </div>

          {/* Borç Verme Nedeni */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Borç Verme Nedeni</label>
            <input
              type="text"
              value={form.reason}
              onChange={e => handleChange('reason', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700"
              placeholder="Neden borç verildi?"
            />
          </div>

          {/* Açıklama */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Açıklama</label>
            <textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Detaylı açıklama"
            />
          </div>

          {/* Notlar */}
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Notlar</label>
            <textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
              rows={2}
              placeholder="Ek notlar"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                saving ? 'bg-primary-300 text-background-50 cursor-not-allowed' : 'btn-primary'
              }`}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-background-50/30 border-t-background-50 animate-spin"></div>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-check-line"></i>
                  Borç Ver
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}